import { useEffect, useMemo, useState } from "react";
import { api, pctToDec as pct2dec, decToPct as dec2pct, isYmd, todayYmd } from "@/lib/api";
import { PageHeader, Btn } from "@/components/Shared";
import { Plus, Mail } from "lucide-react";
import GrantAccessModal from "@/components/GrantAccessModal";
import RateChangeDialog from "@/components/RateChangeDialog";
import { toast } from "sonner";
import { openConfirm } from "@/lib/dialogs";

import PartnersTable from "./partners/PartnersTable";
import PartnerFormModal from "./partners/PartnerFormModal";
import PartnerEmailReconModal from "./partners/PartnerEmailReconModal";
import PartnerRateCardsModal from "./partners/PartnerRateCardsModal";

const EMPTY = {
  partner_id: "", partner_name: "", contact: "", notes: "",
  email: "",
  compensation_model: "fixed_base_rate", revenue_share_pct: 0,
  rate_cards: [],
  gst_applicable: false, gst_rate: 0.18, gstin: "",
  tds_rate: 0.02, pan: "",
  standing_deductions: [],
  fixed_monthly_fee: 0, fixed_fee_start_month: "", fixed_fee_end_month: "",
  reimbursement_target: "Partner",
};

// Compute next available Pxxx id
const nextPartnerId = (rows) => {
  const used = new Set(rows.map((r) => r.partner_id));
  let i = 1;
  while (used.has(`P${String(i).padStart(3, "0")}`)) i++;
  return `P${String(i).padStart(3, "0")}`;
};

export default function Partners() {
  const [rows, setRows] = useState([]);
  const [gateways, setGateways] = useState([]);
  const [meta, setMeta] = useState({ lobs: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [q, setQ] = useState("");
  const [filterComp, setFilterComp] = useState("");
  const [filterGst, setFilterGst] = useState("");
  const [sortBy, setSortBy] = useState({ key: "partner_id", dir: "asc" });
  // Iter31 — partner-level rate-versioning UI. Modal tabs (form / rate-history)
  // + multi-LOB detection in save() + RateChangeDialog after the user picks
  // an effective_from. partner_lob scope is keyed by (partner_id, gateway, lob).
  const [modalTab, setModalTab] = useState("form");
  const [rateHistoryKey, setRateHistoryKey] = useState({ gateway: "", lob: "" });
  const [rateChangePending, setRateChangePending] = useState(null);

  const load = async () => {
    const [r, g, m] = await Promise.all([api.get("/partners"), api.get("/gateways"), api.get("/meta")]);
    setRows(r.data.sort((a, b) => a.partner_id.localeCompare(b.partner_id)));
    setGateways(g.data); setMeta(m.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm({ ...EMPTY, partner_id: nextPartnerId(rows) });
    setEditId(null); setModalTab("form"); setRateHistoryKey({ gateway: "", lob: "" }); setOpen(true);
  };
  const openEdit = (r) => {
    setForm({
      ...EMPTY, ...r,
      compensation_model: r.compensation_model || "fixed_base_rate",
      revenue_share_pct: r.revenue_share_pct ?? 0,
      rate_cards: r.rate_cards || [],
      gst_applicable: !!r.gst_applicable,
      gst_rate: r.gst_rate ?? 0.18,
      tds_rate: r.tds_rate ?? 0.02,
      gstin: r.gstin || "", pan: r.pan || "",
      standing_deductions: r.standing_deductions || [],
      fixed_monthly_fee: r.fixed_monthly_fee || 0,
      fixed_fee_start_month: r.fixed_fee_start_month || "",
      fixed_fee_end_month: r.fixed_fee_end_month || "",
      reimbursement_target: r.reimbursement_target || "Partner",
    });
    setEditId(r.partner_id); setModalTab("form");
    // Default the rate-history selector to the partner's first rate card
    // (gateway, lob) so the Rate History tab loads something on first open.
    const first = (r.rate_cards || [])[0];
    setRateHistoryKey({ gateway: first?.gateway || "", lob: first?.lob || "" });
    setOpen(true);
  };

  const save = async () => {
    // ----- Mandatory-field validation (mirrors product spec) -----
    const missing = [];
    if (!String(form.partner_name || "").trim()) missing.push("Partner Name");
    if (!form.compensation_model) missing.push("Compensation Model");
    if (missing.length) { toast.error(`Please fill: ${missing.join(", ")}`); return; }

    // Compensation-model-specific minimums.
    if (form.compensation_model === "fixed_base_rate") {
      const cards = (form.rate_cards || []).filter((c) => c.gateway && c.lob && c.partner_base_rate != null && c.partner_base_rate !== "");
      if (!cards.length) {
        toast.error("Add at least one Partner base rate (Gateway × LOB) before saving.");
        return;
      }
    }
    if (form.compensation_model === "revenue_share") {
      if (!Number(form.revenue_share_pct)) {
        toast.error("Revenue Share (%) must be greater than 0.");
        return;
      }
    }
    if (form.compensation_model === "fixed_fee") {
      if (!Number(form.fixed_monthly_fee)) {
        toast.error("Fixed monthly fee must be greater than 0.");
        return;
      }
    }

    // GST: if registered, GSTIN + GST Rate are required.
    if (form.gst_applicable) {
      if (!String(form.gstin || "").trim()) { toast.error("GSTIN is required when partner is GST-registered."); return; }
      if (!Number(form.gst_rate)) { toast.error("GST Rate (%) is required when partner is GST-registered."); return; }
    }

    try {
      const payload = {
        ...form,
        gst_rate: Number(form.gst_rate || 0),
        tds_rate: Number(form.tds_rate || 0),
        revenue_share_pct: Number(form.revenue_share_pct || 0),
        rate_cards: form.compensation_model === "fixed_base_rate"
          ? (form.rate_cards || []).map((r) => ({ ...r, partner_base_rate: pct2dec(dec2pct(r.partner_base_rate)) }))
          : [],
        standing_deductions: (form.standing_deductions || []).filter((s) => s.label && Number(s.amount) > 0)
          .map((s) => ({ ...s, amount: Number(s.amount) })),
        fixed_monthly_fee: Number(form.fixed_monthly_fee || 0),
        fixed_fee_start_month: form.fixed_fee_start_month || "",
        fixed_fee_end_month: form.fixed_fee_end_month || "",
      };

      // Iter31 — Partner rate-card change detection. Mirrors the multi-LOB
      // gateway flow: if any (gateway, lob) rate card's partner_base_rate
      // changed on edit, surface RateChangeDialog so admin picks ONE
      // effective_from for ALL changed cards. New cards (no prior version)
      // are created without a dialog — they don't need a versioning step.
      if (editId && form.compensation_model === "fixed_base_rate") {
        const oldRow = rows.find((r) => r.partner_id === editId);
        const oldCards = (oldRow?.rate_cards || []);
        const changes = [];
        for (const nc of payload.rate_cards) {
          if (!nc.gateway || !nc.lob) continue;
          const oc = oldCards.find((c) => c.gateway === nc.gateway && c.lob === nc.lob);
          if (oc && Number(oc.partner_base_rate) !== Number(nc.partner_base_rate)) {
            changes.push({
              lob: `${nc.gateway} · ${nc.lob}`,
              gateway: nc.gateway,
              lobOnly: nc.lob,
              oldRate: Number(oc.partner_base_rate),
              newRate: Number(nc.partner_base_rate),
            });
          }
        }
        if (changes.length >= 1) {
          const first = changes[0];
          setRateChangePending({
            scope: "partner_lob",
            payload, partnerId: editId, partnerName: form.partner_name,
            lob: first.lob, oldRate: first.oldRate, newRate: first.newRate,
            changes,
          });
          return;
        }
      }

      // iter39 — Revenue-share partners: detect a change to revenue_share_pct
      // on edit and route through the same RateChangeDialog so admins can
      // pick the effective_from for the new share. Without this, the new %
      // silently overwrote partner.revenue_share_pct and the calc engine
      // (which reads it via the program's snapshotted partner_share_value)
      // didn't time-travel for historical/reopened periods.
      if (editId && form.compensation_model === "revenue_share") {
        const oldRow = rows.find((r) => r.partner_id === editId);
        const oldPct = Number(oldRow?.revenue_share_pct ?? 0);
        const newPct = Number(payload.revenue_share_pct ?? 0);
        const wasRevShare = (oldRow?.compensation_model || "fixed_base_rate") === "revenue_share";
        if (wasRevShare && oldPct !== newPct && newPct > 0) {
          setRateChangePending({
            scope: "partner_revenue_share",
            payload, partnerId: editId, partnerName: form.partner_name,
            oldRate: oldPct, newRate: newPct,
          });
          return;
        }
      }

      // Note: Partner rate versioning lives on (partner_id, gateway, lob).
      // Rate cards added or rate changes on existing cards trigger the
      // RateChangeDialog above; rate cards REMOVED leave their version
      // history intact (audit trail preserved).
      if (editId) await api.put(`/partners/${editId}`, payload); else await api.post("/partners", payload);
      toast.success("Saved"); setOpen(false); setModalTab("form"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Iter31 — RateChangeDialog confirm callback. Persists partner edit AND
  // posts one rate_versions row per changed (partner_id, gateway, lob).
  // For FUTURE-DATED changes the partner's scalar rate_cards are
  // intentionally restored to OLD values so the directory page stays
  // accurate until the effective date arrives. Calc engine reads via
  // rate_at(), so calculations after the effective date pick up new rates.
  // iter39 — extended to support `partner_revenue_share` scope (single
  // share %, no gateway/lob dimension).
  const confirmRateChange = async ({ effective_from, reason }) => {
    if (!rateChangePending) return;
    const { scope, payload, partnerId, changes, oldRate, newRate } = rateChangePending;
    if (!isYmd(effective_from)) {
      toast.error("Invalid effective date — expected YYYY-MM-DD");
      return;
    }
    const isFuture = effective_from > todayYmd();
    try {
      if (scope === "partner_revenue_share") {
        // For future-dated share changes, keep the partner's scalar
        // `revenue_share_pct` at the OLD value until the effective date
        // arrives — calc engine resolves via rate_at() so historical/today
        // calculations stay correct.
        let putBody = payload;
        if (isFuture) {
          putBody = { ...payload, revenue_share_pct: Number(oldRate) };
        }
        await api.put(`/partners/${partnerId}`, putBody);
        await api.post("/rate-versions", {
          scope: "partner_revenue_share",
          partner_id: partnerId,
          effective_from: `${effective_from}T00:00:00Z`,
          rate_type: "Percentage",
          rate_value: Number(newRate),
          reason: reason || "Partner revenue share change",
        });
        if (isFuture) {
          toast.success(`Future-dated revenue share saved · activates on ${effective_from}`, {
            description: `${dec2pct(oldRate)}% → ${dec2pct(newRate)}% — current rate stays unchanged until then.`,
            duration: 7000,
          });
        } else {
          toast.success(`Revenue share updated effective ${effective_from}`);
        }
        setRateChangePending(null);
        setOpen(false); setModalTab("form");
        load();
        return;
      }
      // Default flow: partner_lob (fixed_base_rate rate cards)
      let putBody = payload;
      if (isFuture) {
        const restoreByKey = Object.fromEntries(
          changes.map((c) => [`${c.gateway}|${c.lobOnly}`, Number(c.oldRate)])
        );
        putBody = {
          ...payload,
          rate_cards: payload.rate_cards.map((r) => {
            const k = `${r.gateway}|${r.lob}`;
            return k in restoreByKey ? { ...r, partner_base_rate: restoreByKey[k] } : r;
          }),
        };
      }
      await api.put(`/partners/${partnerId}`, putBody);
      for (const c of changes) {
        await api.post("/rate-versions", {
          scope: "partner_lob",
          partner_id: partnerId,
          gateway: c.gateway,
          lob: c.lobOnly,
          effective_from: `${effective_from}T00:00:00Z`,
          rate_type: "Basis Points",
          rate_value: Number(c.newRate),
          reason: reason || `Partner rate change · ${c.lob}`,
        });
      }
      const summary = changes.length === 1
        ? changes[0].lob
        : `${changes.length} rate cards`;
      if (isFuture) {
        toast.success(`Future-dated rate version saved · activates on ${effective_from}`, {
          description: `${summary} — current display rates stay unchanged until then.`,
          duration: 7000,
        });
      } else {
        toast.success(`Saved · ${summary} updated effective ${effective_from}`);
      }
      setRateChangePending(null);
      setOpen(false); setModalTab("form");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!await openConfirm({ title: "Delete partner?", message: "This will remove the partner and detach any merchants linked to them. You can't undo this.", confirmLabel: "Delete partner", danger: true })) return;
    await api.delete(`/partners/${id}`); toast.success("Deleted"); load();
  };

  // Email recon — modal-driven (single partner OR bulk to all eligible).
  // Bulk: pick period -> fetch /email/partner-recon/preview -> table with
  // per-partner net payout + checkbox -> confirm send.
  const [emailModal, setEmailModal] = useState(null);  // null | {target, partner?, period, stage, preview, selected}
  const [grantFor, setGrantFor] = useState(null);  // partner row → opens GrantAccessModal
  // Read-only popup for spot-checking rate cards without entering the Edit
  // modal. Clicking "Open in editor" inside this popup forwards to openEdit.
  const [rateCardsFor, setRateCardsFor] = useState(null);
  const [emailBusy, setEmailBusy] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);

  // Track which partner_ids already have a portal access user. Lit by the
  // KeyRound icon turning green so admin can see at a glance who's already
  // onboarded. Bumps whenever the modal closes after a grant/revoke so the
  // badge updates without needing a full page refresh.
  const [grantedPartnerIds, setGrantedPartnerIds] = useState(new Set());
  const [grantBump, setGrantBump] = useState(0);
  useEffect(() => {
    let mounted = true;
    api.get("/admin/access/list").then(({ data }) => {
      if (!mounted) return;
      const ids = new Set();
      (data || []).filter((u) => u.role === "partner" && u.linked_partner_id).forEach((u) => {
        ids.add(String(u.linked_partner_id));
      });
      setGrantedPartnerIds(ids);
    }).catch(() => { /* admin-only — silent for non-admins */ });
    return () => { mounted = false; };
  }, [rows, grantBump]);
  const partnerHasAccess = (p) => grantedPartnerIds.has(String(p.partner_id));

  const lastMonthIso = () => {
    const now = new Date();
    const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return lm.toISOString().slice(0, 7);
  };

  const openEmailOne = (p) => {
    if (!p.email) return;
    setEmailModal({ target: "one", partner: p, period: lastMonthIso(), stage: "pick" });
  };
  const openEmailBulk = () => {
    setEmailModal({ target: "bulk", period: lastMonthIso(), stage: "pick", preview: null, selected: {} });
  };

  const fetchPreview = async () => {
    if (!emailModal?.period || !/^\d{4}-\d{2}$/.test(emailModal.period)) { toast.error("Pick a valid month"); return; }
    setPreviewBusy(true);
    try {
      const { data } = await api.post("/email/partner-recon/preview", { period: emailModal.period });
      // default-select every row whose net_payout > 0
      const sel = {};
      (data.rows || []).forEach((r) => { sel[r.partner_id] = !r.will_skip; });
      setEmailModal((m) => ({ ...m, stage: "preview", preview: data.rows || [], selected: sel }));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (e?.message || "Failed to load preview");
      toast.error(msg);
    }
    setPreviewBusy(false);
  };

  const sendEmail = async () => {
    if (!emailModal?.period || !/^\d{4}-\d{2}$/.test(emailModal.period)) { toast.error("Pick a valid month"); return; }
    setEmailBusy(true);
    try {
      if (emailModal.target === "one") {
        const res = await api.post("/email/partner-recon", { partner_id: emailModal.partner.partner_id, period: emailModal.period });
        toast.success(`Recon emailed to ${res.data.to}`);
        setEmailModal(null);
      } else {
        const targets = (emailModal.preview || []).filter((r) => emailModal.selected[r.partner_id]);
        if (!targets.length) { toast.error("Pick at least one partner to send to"); setEmailBusy(false); return; }
        let sent = 0, failed = 0;
        for (const p of targets) {
          try { await api.post("/email/partner-recon", { partner_id: p.partner_id, period: emailModal.period }); sent += 1; }
          catch { failed += 1; }
        }
        toast.success(`${sent} sent${failed ? ` · ${failed} failed` : ""}`);
        setEmailModal(null);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (e?.message || "Failed");
      toast.error(msg);
    }
    setEmailBusy(false);
  };
  const toggleSelect = (id) => setEmailModal((m) => ({ ...m, selected: { ...m.selected, [id]: !m.selected[id] } }));
  const toggleSelectAll = (val) => setEmailModal((m) => ({ ...m, selected: Object.fromEntries((m.preview || []).map((r) => [r.partner_id, val])) }));
  const eligibleCount = rows.filter((r) => r.email && r.partner_id !== "P000").length;
  const selectedCount = emailModal?.preview ? emailModal.preview.filter((r) => emailModal.selected[r.partner_id]).length : 0;
  const previewTotal = emailModal?.preview ? emailModal.preview.filter((r) => emailModal.selected[r.partner_id]).reduce((s, r) => s + Number(r.net_payout || 0), 0) : 0;

  // ----- Rate-card + deduction helpers (live here because they close over
  //       `form` + `setForm` + lookups like `gateways` / `meta`) ----------
  const addCard = () => setForm({ ...form, rate_cards: [...(form.rate_cards || []), { gateway: gateways[0]?.gateway_name || "", lob: meta.lobs?.[0] || "", partner_base_rate: 0 }] });
  const updateCard = (i, field, val) => { const next = [...(form.rate_cards || [])]; next[i] = { ...next[i], [field]: val }; setForm({ ...form, rate_cards: next }); };
  const removeCard = (i) => setForm({ ...form, rate_cards: form.rate_cards.filter((_, j) => j !== i) });

  const genId = () => Math.random().toString(36).slice(2, 11);
  const addDeduction = () => setForm({ ...form, standing_deductions: [...(form.standing_deductions || []), { id: genId(), label: "", amount: 0, start_month: "", end_month: "", notes: "" }] });
  const updateDeduction = (i, field, val) => { const next = [...(form.standing_deductions || [])]; next[i] = { ...next[i], [field]: val }; setForm({ ...form, standing_deductions: next }); };
  const removeDeduction = (i) => setForm({ ...form, standing_deductions: form.standing_deductions.filter((_, j) => j !== i) });

  const toggleSort = (key) => setSortBy((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });

  const filtered = useMemo(() => {
    let list = rows.filter((r) => {
      if (q) {
        const s = `${r.partner_id} ${r.partner_name} ${r.contact || ""}`.toLowerCase();
        if (!s.includes(q.toLowerCase())) return false;
      }
      if (filterComp && r.compensation_model !== filterComp) return false;
      if (filterGst === "yes" && !r.gst_applicable) return false;
      if (filterGst === "no" && r.gst_applicable) return false;
      return true;
    });
    const { key, dir } = sortBy;
    const mul = dir === "asc" ? 1 : -1;
    list = [...list].sort((a, b) => {
      let va, vb;
      switch (key) {
        case "partner_id": va = a.partner_id || ""; vb = b.partner_id || ""; break;
        case "partner_name": va = (a.partner_name || "").toLowerCase(); vb = (b.partner_name || "").toLowerCase(); break;
        case "compensation": va = a.compensation_model || ""; vb = b.compensation_model || ""; break;
        case "tds": return ((a.tds_rate ?? 0) - (b.tds_rate ?? 0)) * mul;
        case "gst": return ((a.gst_applicable ? (a.gst_rate ?? 0) : -1) - (b.gst_applicable ? (b.gst_rate ?? 0) : -1)) * mul;
        default: va = a[key] || ""; vb = b[key] || "";
      }
      return String(va).localeCompare(String(vb)) * mul;
    });
    return list;
  }, [rows, q, filterComp, filterGst, sortBy]);

  const clearFilters = () => { setQ(""); setFilterComp(""); setFilterGst(""); };
  const hasFilters = q || filterComp || filterGst;
  const closeForm = () => { setOpen(false); setModalTab("form"); };

  return (
    <div data-testid="partners-page">
      <PageHeader
        title="Partners"
        actions={<>
          <Btn variant="ghost" testid="email-bulk-btn" onClick={openEmailBulk} disabled={!eligibleCount}><Mail size={14} /> Email recon to all ({eligibleCount})</Btn>
          <Btn variant="accent" testid="add-partner-btn" onClick={openNew}><Plus size={14} /> New Partner</Btn>
        </>}
      />
      <div className="p-4 sm:p-10">
        <PartnersTable
          filtered={filtered}
          hasFilters={hasFilters}
          q={q} setQ={setQ}
          filterComp={filterComp} setFilterComp={setFilterComp}
          filterGst={filterGst} setFilterGst={setFilterGst}
          sortBy={sortBy} onToggleSort={toggleSort}
          onClearFilters={clearFilters}
          partnerHasAccess={partnerHasAccess}
          onEmailOne={openEmailOne}
          onEdit={openEdit}
          onRemove={remove}
          onGrant={setGrantFor}
          onViewRateCards={setRateCardsFor}
        />
      </div>

      <PartnerFormModal
        open={open}
        onClose={closeForm}
        editId={editId}
        form={form} setForm={setForm}
        modalTab={modalTab} setModalTab={setModalTab}
        rateHistoryKey={rateHistoryKey} setRateHistoryKey={setRateHistoryKey}
        gateways={gateways}
        onSave={save}
        addCard={addCard} updateCard={updateCard} removeCard={removeCard}
        addDeduction={addDeduction} updateDeduction={updateDeduction} removeDeduction={removeDeduction}
      />

      <RateChangeDialog
        open={!!rateChangePending}
        onClose={() => setRateChangePending(null)}
        onConfirm={confirmRateChange}
        oldRate={rateChangePending?.oldRate}
        newRate={rateChangePending?.newRate}
        changes={rateChangePending?.scope === "partner_revenue_share" ? undefined : rateChangePending?.changes}
        entityLabel={rateChangePending ? (
          rateChangePending.scope === "partner_revenue_share"
            ? `Partner ${rateChangePending.partnerId}${rateChangePending.partnerName ? ` · ${rateChangePending.partnerName}` : ""} · Revenue Share`
            : `Partner ${rateChangePending.partnerId}${rateChangePending.partnerName ? ` · ${rateChangePending.partnerName}` : ""}${rateChangePending.changes?.length === 1 ? ` · ${rateChangePending.lob}` : ""}`
        ) : ""}
      />

      <PartnerEmailReconModal
        emailModal={emailModal} setEmailModal={setEmailModal}
        eligibleCount={eligibleCount}
        selectedCount={selectedCount}
        previewTotal={previewTotal}
        emailBusy={emailBusy} previewBusy={previewBusy}
        onFetchPreview={fetchPreview}
        onSend={sendEmail}
        onToggleSelect={toggleSelect}
        onToggleSelectAll={toggleSelectAll}
      />

      <GrantAccessModal
        open={!!grantFor}
        onClose={() => { setGrantFor(null); setGrantBump((n) => n + 1); }}
        role="partner"
        subject={grantFor || {}}
      />

      <PartnerRateCardsModal
        partner={rateCardsFor}
        onClose={() => setRateCardsFor(null)}
        onEdit={() => {
          const p = rateCardsFor;
          setRateCardsFor(null);
          if (p) openEdit(p);
        }}
      />
    </div>
  );
}
